/**
 * Trade adapters for the shared sketch core.
 *
 * Each adapter teaches the same drawing engine one trade's language:
 * what a straight run is made of, what gets placed at a turn, what a point
 * item is, and how the drawing turns into a priced takeoff.  All prices and
 * hours come from the offline material catalog — nothing here is a guess made
 * at runtime and nothing needs the network.
 */
import {
  AWG_SIZES,
  DEVICES,
  RACEWAYS,
  TRADE_SIZES,
  WIRE_TYPES,
  type AwgSize,
  type InsulationId,
  type RacewayId,
  type TradeSize,
} from "@/lib/electrical-data";
import { getHvacItem, HVAC_ITEMS } from "@/lib/hvac-data";
import { getStructuralItem, STRUCTURAL_ITEMS } from "@/lib/structural-data";
import {
  catalogKeys,
  electricalCatalogItem,
  hvacCatalogItem,
  structuralCatalogItem,
} from "@/lib/material-catalog";
import {
  mergeLines,
  runArea,
  runLength,
  runPerimeter,
  runTurns,
  type SketchDoc,
  type TakeoffLine,
  type TradeSketchAdapter,
} from "./index";

const num = (v: unknown, fallback: number) =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;
const str = (v: unknown, fallback: string) => (typeof v === "string" && v ? v : fallback);

/* ================================================================== */
/* ELECTRICAL — conduit runs, bends, conductors, devices               */
/* ================================================================== */

const ELEC_RACEWAYS: RacewayId[] = ["emt", "imc", "rmc", "pvc40", "fmc"];
const ELEC_SIZES: TradeSize[] = ['1/2"', '3/4"', '1"', '1-1/4"', '1-1/2"', '2"', '3"', '4"'];

const gearSort = (a: [string, typeof DEVICES[string]], b: [string, typeof DEVICES[string]]) =>
  Number(!!b[1].gear) - Number(!!a[1].gear);

/** Conduit fill + conductor math shared by the drawing badge and the takeoff. */
function elecRun(run: { kind: string; pts: unknown[]; meta?: Record<string, unknown> }) {
  const [, raceway, size] = run.kind.split(":") as ["econd", RacewayId, TradeSize];
  const spec = RACEWAYS[raceway];
  const wires = Math.max(0, Math.round(num(Number(run.meta?.wires), 3)));
  const awg = str(run.meta?.awg, "12") as AwgSize;
  const ins = str(run.meta?.ins, "thhn") as InsulationId;
  const wire = WIRE_TYPES[ins];
  const area = spec?.area[size] ?? 0;
  const fill = area && wire ? (wire.area[awg] * wires) / area : 0;
  const limit = wires === 1 ? 0.53 : wires === 2 ? 0.31 : 0.4;
  return { raceway, size, spec, wires, awg, ins, wire, fill, limit, over: fill > limit };
}

const isHomerun = (meta?: Record<string, unknown>) => str(meta?.hr, "no") === "yes";

export const electricalSketchAdapter: TradeSketchAdapter = {
  id: "electrical",
  title: "Conduit Sketch",
  subtitle: "Draw conduit, wire, panels and devices — fill, bends and cost take off automatically",
  storageKey: "tt-sketch-electrical",
  estimateKey: "tt-pipe-electrical",
  estimateLabel: "Electrical estimate",
  markPrefix: "C",
  markerPrefix: "E",
  runLabel: "Conduit",
  markerLabel: "Panel / device",
  sections: [
    { id: "conduit", label: "Conduit & raceway" },
    { id: "wire", label: "Wire & cable" },
    { id: "gear", label: "Panels & gear" },
    { id: "devices", label: "Devices, boxes & fixtures" },
  ],
  runItems: ELEC_RACEWAYS.flatMap((r) =>
    ELEC_SIZES.filter((s) => RACEWAYS[r].area[s] != null).map((s) => ({
      key: catalogKeys.conduit(r, s),
      label: `${s} ${RACEWAYS[r].label}`,
      short: `${s} ${RACEWAYS[r].short}`,
      size: s,
      group: RACEWAYS[r].label,
    })),
  ),
  markerItems: Object.entries(DEVICES)
    .sort(gearSort)
    .map(([k, d]) => ({
      key: catalogKeys.device(k),
      label: d.label,
      short: d.label.split(",")[0],
      group: d.gear ? "Panels & gear" : "Devices, boxes & fixtures",
    })),
  runOptions: [
    {
      id: "wires",
      label: "Conductors",
      default: "3",
      options: [1, 2, 3, 4, 5, 6, 9, 12].map((n) => ({ value: String(n), label: `${n} wire` })),
    },
    {
      id: "awg",
      label: "Wire size",
      default: "12",
      options: AWG_SIZES.map((a) => ({ value: a, label: `#${a}` })),
    },
    {
      id: "ins",
      label: "Insulation",
      default: "thhn",
      options: (Object.keys(WIRE_TYPES) as InsulationId[]).map((i) => ({
        value: i,
        label: WIRE_TYPES[i].label,
      })),
    },
    {
      id: "hr",
      label: "Home run",
      default: "no",
      options: [
        { value: "no", label: "Branch run" },
        { value: "yes", label: "Home run to panel" },
      ],
    },
  ],
  strokeFor: (kind) => {
    const size = kind.split(":")[2] as TradeSize;
    const idx = Math.max(0, TRADE_SIZES.indexOf(size));
    return 3 + idx * 0.6;
  },
  colorFor: (kind) => {
    const r = kind.split(":")[1] as RacewayId;
    return r === "pvc40" || r === "pvc80"
      ? "hsl(var(--chart-3, 200 70% 45%))"
      : r === "fmc"
        ? "hsl(var(--muted-foreground))"
        : "hsl(var(--primary))";
  },
  symbolFor(kind) {
    const k = kind.replace(/^edev:/, "");
    if (k.startsWith("panel") || k.startsWith("mdp") || k.startsWith("switchboard")) return "panel";
    if (k.startsWith("xfmr")) return "transformer";
    if (k.startsWith("disconnect") || k.startsWith("starter") || k.startsWith("meter")) return "disconnect";
    if (k.startsWith("switch") || k.startsWith("occ")) return "switch";
    if (k.startsWith("duplex") || k.startsWith("gfci") || k.startsWith("recept")) return "receptacle";
    if (k.startsWith("fixture") || k.startsWith("hi-bay")) return "light";
    if (k.startsWith("ground")) return "ground";
    if (k.startsWith("box")) return "box";
    return "square";
  },
  runBadge(run) {
    const c = elecRun(run);
    if (!c.spec) return null;
    const wire = c.wires > 0 ? ` · ${c.wires}#${c.awg}` : "";
    const fill = c.wires > 0 ? ` · ${(c.fill * 100).toFixed(0)}% fill` : "";
    return {
      text: `${c.size} ${c.spec.short}${wire}${fill}${isHomerun(run.meta) ? " · HR" : ""}`,
      warn: c.over,
    };
  },
  runIsHomerun: (run) => isHomerun(run.meta),
  takeoff(doc) {
    const lines: TakeoffLine[] = [];
    for (const run of doc.runs) {
      const c = elecRun(run);
      if (!c.spec) continue;
      const ft = runLength(run);
      const turns = runTurns(run);
      // each bend adds roughly its radius back into the pull
      const bendFt = turns.reduce((s, t) => s + (t.angle >= 90 ? 0.5 : 0.25), 0);
      const cut = ft + bendFt;
      const item = electricalCatalogItem(run.kind);
      lines.push({
        key: `${run.id}-conduit`,
        qtyKey: run.kind,
        section: "conduit",
        label: item?.description ?? run.kind,
        size: c.size,
        unit: "ft",
        qty: cut,
        unitCost: item?.price ?? 0,
        hours: (c.spec.hrs[c.size] ?? 0) * cut,
        from: [run.mark],
      });

      if (c.wires > 0 && c.wire) {
        // 2 ft of make-up each end, plus 4 ft of gutter length in the panel on a home run
        const tails = 4 + (isHomerun(run.meta) ? 4 : 0);
        const pullFt = (cut + tails) * c.wires;
        const wkey = catalogKeys.wire(c.ins, c.awg);
        const witem = electricalCatalogItem(wkey);
        lines.push({
          key: `${run.id}-wire`,
          qtyKey: wkey,
          section: "wire",
          label: witem?.description ?? wkey,
          size: `#${c.awg}`,
          unit: "ft",
          qty: pullFt,
          unitCost: witem?.price ?? 0,
          hours: c.wire.hrs[c.awg] * pullFt,
          from: [run.mark],
          // NEC Ch.9 Table 1 — 40 % for more than two conductors
          warn: c.over
            ? `${run.mark}: ${(c.fill * 100).toFixed(0)} % fill over the ${(c.limit * 100).toFixed(0)} % limit`
            : undefined,
        });
      }
    }
    for (const m of doc.markers) {
      const key = m.kind.replace(/^edev:/, "");
      const d = DEVICES[key];
      const item = electricalCatalogItem(m.kind);
      lines.push({
        key: `${m.id}-dev`,
        qtyKey: m.kind,
        section: d?.gear ? "gear" : "devices",
        label: item?.description ?? d?.label ?? m.kind,
        size: "—",
        unit: "ea",
        qty: 1,
        unitCost: item?.price ?? 0,
        hours: d?.hrs ?? 0,
        from: [m.mark],
      });
    }
    return mergeLines(lines);
  },
  summary(doc) {
    const ft = doc.runs.reduce((s, r) => s + runLength(r), 0);
    const bends = doc.runs.reduce((s, r) => s + runTurns(r).length, 0);
    const gear = doc.markers.filter((m) => DEVICES[m.kind.replace(/^edev:/, "")]?.gear).length;
    const devs = doc.markers.length - gear;
    const over = doc.runs.filter((r) => elecRun(r).over).length;
    return `${ft.toFixed(1)} ft conduit · ${bends} bend${bends === 1 ? "" : "s"} · ${gear} gear · ${devs} device${devs === 1 ? "" : "s"}${over ? ` · ${over} over fill` : ""}`;
  },
};

/* ================================================================== */
/* DUCT — rectangular / round duct, elbows, taps, air devices          */
/* ================================================================== */

const DUCT_RUN_IDS = [
  "rnd-06", "rnd-08", "rnd-10", "rnd-12", "rnd-14", "rnd-16", "rnd-20", "rnd-24",
  "rect-sm", "rect-md", "rect-lg", "flex-06", "flex-08", "flex-10",
];

/** Outside surface area per foot of run — drives liner / wrap square footage. */
const DUCT_SQFT_PER_FT: Record<string, number> = {
  "rnd-06": 1.6, "rnd-08": 2.1, "rnd-10": 2.6, "rnd-12": 3.1, "rnd-14": 3.7,
  "rnd-16": 4.2, "rnd-20": 5.2, "rnd-24": 6.3,
  "rect-sm": 6.7, "rect-md": 11.3, "rect-lg": 16.0,
  "flex-06": 1.6, "flex-08": 2.1, "flex-10": 2.6,
};

const DUCT_MARKER_IDS = HVAC_ITEMS.filter(
  (i) => i.section === "registers" || i.section === "coils" || i.section === "blowers",
).map((i) => i.id);

const isRound = (id: string) => id.startsWith("rnd") || id.startsWith("flex");

export const ductSketchAdapter: TradeSketchAdapter = {
  id: "duct",
  title: "Duct Sketch",
  subtitle: "Lay out duct runs — elbows, taps, liner and air devices take off automatically",
  storageKey: "tt-sketch-duct",
  estimateKey: "tt-pipe-hvac",
  estimateLabel: "HVAC estimate",
  markPrefix: "D",
  markerPrefix: "A",
  runLabel: "Duct",
  markerLabel: "Air device / unit",
  runItems: DUCT_RUN_IDS.map((id) => {
    const it = getHvacItem(id)!;
    return { key: id, label: `${it.size} ${it.label}`, short: `${it.size}`, size: it.size };
  }),
  markerItems: DUCT_MARKER_IDS.map((id) => {
    const it = getHvacItem(id)!;
    return { key: id, label: `${it.label} ${it.size}`, short: it.label.split(",")[0], size: it.size };
  }),
  runOptions: [
    {
      id: "insul",
      label: "Liner / wrap",
      default: "none",
      options: [
        { value: "none", label: "Bare duct" },
        { value: "liner-100", label: '1" liner' },
        { value: "wrap-150", label: '1-1/2" wrap' },
      ],
    },
  ],
  strokeFor: (kind) => {
    const it = getHvacItem(kind);
    const sq = DUCT_SQFT_PER_FT[kind] ?? 3;
    return Math.min(16, 3 + sq * (it && isRound(kind) ? 0.9 : 0.6));
  },
  colorFor: (kind) =>
    kind.startsWith("flex")
      ? "hsl(var(--muted-foreground))"
      : isRound(kind)
        ? "hsl(var(--primary))"
        : "hsl(var(--chart-2, 25 85% 50%))",
  takeoff(doc) {
    const lines: TakeoffLine[] = [];
    for (const run of doc.runs) {
      const it = getHvacItem(run.kind);
      if (!it) continue;
      const ft = runLength(run);
      const item = hvacCatalogItem(run.kind);
      lines.push({
        key: `${run.id}-duct`,
        qtyKey: run.kind,
        label: `${it.label} ${it.size}`,
        size: it.size,
        unit: "ft",
        qty: ft,
        unitCost: item.price,
        hours: it.hours * ft,
        from: [run.mark],
      });

      const turns = runTurns(run);
      if (turns.length) {
        const elbId = isRound(run.kind) ? "elb-rnd" : "elb-rect";
        const elb = getHvacItem(elbId)!;
        lines.push({
          key: `${run.id}-elb`,
          qtyKey: elbId,
          label: elb.label,
          size: elb.size,
          unit: "ea",
          qty: turns.length,
          unitCost: hvacCatalogItem(elbId).price,
          hours: elb.hours * turns.length,
          from: [run.mark],
        });
      }

      const insul = str(run.meta?.insul, "none");
      if (insul !== "none") {
        const ins = getHvacItem(insul);
        const sq = (DUCT_SQFT_PER_FT[run.kind] ?? 3) * ft;
        if (ins) {
          lines.push({
            key: `${run.id}-ins`,
            qtyKey: insul,
            label: `${ins.label} ${ins.size}`,
            size: ins.size,
            unit: "sq ft",
            qty: sq,
            unitCost: hvacCatalogItem(insul).price,
            hours: ins.hours * sq,
            from: [run.mark],
          });
        }
      }
    }

    // every air device is fed by a spin-in takeoff with a damper
    const feeds = doc.markers.filter((m) => getHvacItem(m.kind)?.section === "registers").length;
    if (feeds) {
      const t = getHvacItem("takeoff")!;
      lines.push({
        key: "takeoffs",
        qtyKey: "takeoff",
        label: t.label,
        size: t.size,
        unit: "ea",
        qty: feeds,
        unitCost: hvacCatalogItem("takeoff").price,
        hours: t.hours * feeds,
      });
    }

    for (const m of doc.markers) {
      const it = getHvacItem(m.kind);
      if (!it) continue;
      lines.push({
        key: `${m.id}-dev`,
        qtyKey: m.kind,
        label: `${it.label} ${it.size}`,
        size: it.size,
        unit: "ea",
        qty: 1,
        unitCost: hvacCatalogItem(m.kind).price,
        hours: it.hours,
        from: [m.mark],
      });
    }
    return mergeLines(lines);
  },
  summary(doc) {
    const ft = doc.runs.reduce((s, r) => s + runLength(r), 0);
    const elbows = doc.runs.reduce((s, r) => s + runTurns(r).length, 0);
    return `${ft.toFixed(1)} ft duct · ${elbows} elbow${elbows === 1 ? "" : "s"} · ${doc.markers.length} device${doc.markers.length === 1 ? "" : "s"}`;
  },
};

/* ================================================================== */
/* STRUCTURAL — framing members, connections, embeds                   */
/* ================================================================== */

const MEMBER_IDS = [
  "w8x10", "w8x18", "w10x22", "w12x26", "w14x30", "w16x40", "w21x62",
  "hss4", "hss6", "c8", "l3", "l4", "joist", "safety-rail",
];
const REBAR_IDS = ["rb-3", "rb-4", "rb-5", "rb-6", "rb-7", "rb-8", "rb-9", "rb-10", "rb-11"];
const STRUCT_MARKER_IDS = [
  "base-plate", "embed", "lintel", "field-weld", "bolt-a325",
  "ab-1-2", "ab-3-4", "ab-1", "wedge-1-2", "wedge-5-8", "epoxy-5-8", "dowel", "chair",
];

/** Closed pours drawn as an outline: slab, pad, equipment base. */
const POUR_AREAS: Record<string, { label: string; short: string; slab: boolean }> = {
  "pour:slab": { label: "Slab on grade", short: "Slab", slab: true },
  "pour:sog-topping": { label: "Topping slab / housekeeping pad", short: "Topping", slab: true },
  "pour:pad": { label: "Spread footing / equipment pad", short: "Pad", slab: false },
};
/** Linear pours drawn as a run: strip footing, grade beam, wall. */
const POUR_LINES: Record<string, { label: string; short: string; wall: boolean }> = {
  "pour:footing": { label: "Strip footing", short: "Footing", wall: false },
  "pour:grade-beam": { label: "Grade beam", short: "Gr. beam", wall: false },
  "pour:wall": { label: "Foundation wall", short: "Wall", wall: true },
};

const isPourArea = (kind: string) => kind in POUR_AREAS;
const isPourLine = (kind: string) => kind in POUR_LINES;
const isPour = (kind: string) => isPourArea(kind) || isPourLine(kind);
const isRebarRun = (kind: string) => REBAR_IDS.includes(kind);

/** Bar spacing choices for a mat, in inches (0 = welded wire or none). */
const MAT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "none", label: "No reinforcing" },
  { value: "wwr-6610", label: "6x6 W1.4 mesh" },
  { value: "wwr-6644", label: "6x6 W2.9 mesh" },
  { value: "rb-4@12", label: "#4 @ 12\" each way" },
  { value: "rb-4@16", label: "#4 @ 16\" each way" },
  { value: "rb-5@12", label: "#5 @ 12\" each way" },
  { value: "rb-5@16", label: "#5 @ 16\" each way" },
];

const inchOpts = (vals: number[]) => vals.map((v) => ({ value: String(v), label: `${v}"` }));

/** Section heading comes straight from the catalog row, so it matches the estimate. */
const sectionOf = (id: string) => getStructuralItem(id)?.section ?? "misc";

/** One priced structural row from a catalog id. */
function sRow(
  key: string,
  id: string,
  qty: number,
  from?: string[],
  hoursOverride?: number,
): TakeoffLine | null {
  const it = getStructuralItem(id);
  if (!it || qty <= 0) return null;
  return {
    key,
    qtyKey: id,
    section: sectionOf(id),
    label: it.label,
    size: it.size,
    unit: it.unit === "cy" ? "cu yd" : it.unit === "sqft" ? "sq ft" : it.unit,
    qty,
    unitCost: structuralCatalogItem(id).price,
    hours: hoursOverride ?? it.hours * qty,
    from,
  };
}

export const structuralSketchAdapter: TradeSketchAdapter = {
  id: "structural",
  title: "Structural Sketch",
  subtitle:
    "Outline concrete pours, run rebar and steel, drop anchors — yards, bar footage, weight and connections take off automatically",
  storageKey: "tt-sketch-structural",
  estimateKey: "tt-pipe-structural",
  estimateLabel: "Structural estimate",
  markPrefix: "M",
  markerPrefix: "S",
  runLabel: "Pour / member",
  markerLabel: "Anchor / connection",
  sections: [
    { id: "concrete", label: "Concrete, forms & finish" },
    { id: "rebar", label: "Reinforcing steel" },
    { id: "steel", label: "Structural steel" },
    { id: "misc", label: "Anchors, bolts & misc iron" },
  ],
  runItems: [
    ...Object.entries(POUR_AREAS).map(([key, p]) => ({
      key,
      label: `${p.label} — outline the pour`,
      short: p.short,
      group: "Concrete pours (closed outline)",
    })),
    ...Object.entries(POUR_LINES).map(([key, p]) => ({
      key,
      label: `${p.label} — draw the centreline`,
      short: p.short,
      group: "Concrete, linear",
    })),
    ...REBAR_IDS.map((id) => {
      const it = getStructuralItem(id)!;
      return { key: id, label: `${it.label} ${it.size}`, short: it.size, size: it.size, group: "Reinforcing bar" };
    }),
    ...MEMBER_IDS.map((id) => {
      const it = getStructuralItem(id)!;
      return {
        key: id,
        label: `${it.label} ${it.size}`,
        short: it.size === "—" ? it.label : it.size,
        size: it.size,
        group: "Structural steel",
      };
    }),
  ],
  markerItems: STRUCT_MARKER_IDS.map((id) => {
    const it = getStructuralItem(id)!;
    return {
      key: id,
      label: it.size === "—" ? it.label : `${it.label} ${it.size}`,
      short: it.label.split(",")[0],
      size: it.size,
      group: sectionOf(id) === "rebar" ? "Rebar accessories" : "Anchors & connections",
    };
  }),
  runOptions: [
    {
      id: "mix",
      label: "Concrete mix",
      default: "mix-4000",
      options: [
        { value: "mix-3000", label: "3000 psi" },
        { value: "mix-4000", label: "4000 psi" },
        { value: "mix-5000", label: "5000 psi" },
      ],
    },
    { id: "thick", label: "Thickness / width", default: "6", options: inchOpts([4, 5, 6, 8, 10, 12, 16, 18, 24, 30, 36]) },
    { id: "depth", label: "Depth / height", default: "12", options: inchOpts([8, 12, 16, 24, 30, 36, 48, 72, 96, 120]) },
    { id: "mat", label: "Reinforcing", default: "rb-4@16", options: MAT_OPTIONS },
    {
      id: "bars",
      label: "Continuous bars",
      default: "4",
      options: [0, 2, 3, 4, 5, 6, 8].map((n) => ({ value: String(n), label: n ? `${n} bars` : "None" })),
    },
    {
      id: "finish",
      label: "Slab finish",
      default: "finish-broom",
      options: [
        { value: "none", label: "No finish" },
        { value: "finish-broom", label: "Broom finish" },
        { value: "finish-trowel", label: "Hard trowel" },
      ],
    },
    {
      id: "role",
      label: "Member role",
      default: "beam",
      options: [
        { value: "beam", label: "Beam" },
        { value: "column", label: "Column" },
        { value: "brace", label: "Brace" },
        { value: "girt", label: "Girt / purlin" },
      ],
    },
  ],
  isClosed: (kind) => isPourArea(kind),
  dashFor: (kind) => (isRebarRun(kind) ? "7 4" : undefined),
  fillFor: (kind) =>
    kind === "pour:pad" ? "hsl(var(--muted-foreground) / 0.18)" : "hsl(var(--primary) / 0.12)",
  strokeFor: (kind) =>
    isPourArea(kind)
      ? 2
      : isPourLine(kind)
        ? 8
        : isRebarRun(kind)
          ? 2.5
          : kind.startsWith("w2")
            ? 9
            : kind.startsWith("w1")
              ? 7
              : kind.startsWith("w")
                ? 6
                : 4.5,
  colorFor: (kind) =>
    isPour(kind)
      ? "hsl(var(--muted-foreground))"
      : isRebarRun(kind)
        ? "hsl(var(--chart-3, 200 70% 45%))"
        : kind === "safety-rail"
          ? "hsl(var(--muted-foreground))"
          : kind.startsWith("hss")
            ? "hsl(var(--chart-2, 25 85% 50%))"
            : "hsl(var(--primary))",
  runBadge(run) {
    const t = num(Number(run.meta?.thick), 6);
    if (isPourArea(run.kind)) {
      const a = runArea(run);
      const cy = (a * (t / 12)) / 27;
      return { text: `${a.toFixed(0)} sq ft · ${t}" · ${cy.toFixed(2)} cu yd` };
    }
    if (isPourLine(run.kind)) {
      const d = num(Number(run.meta?.depth), 12);
      const cy = (runLength(run) * (t / 12) * (d / 12)) / 27;
      return { text: `${t}"x${d}" · ${cy.toFixed(2)} cu yd` };
    }
    if (isRebarRun(run.kind)) {
      const it = getStructuralItem(run.kind);
      return { text: `${it?.size ?? ""} · ${runLength(run).toFixed(1)} ft` };
    }
    const it = getStructuralItem(run.kind);
    if (!it) return null;
    const role = str(run.meta?.role, "beam");
    return { text: `${it.size} · ${role}` };
  },
  takeoff(doc) {
    const lines: Array<TakeoffLine | null> = [];
    let joints = 0;

    for (const run of doc.runs) {
      const from = [run.mark];
      const mix = str(run.meta?.mix, "mix-4000");
      const thick = num(Number(run.meta?.thick), 6);
      const depth = num(Number(run.meta?.depth), 12);
      const mat = str(run.meta?.mat, "none");
      const finish = str(run.meta?.finish, "none");

      /* ---- concrete pours ------------------------------------------ */
      if (isPour(run.kind)) {
        const area = isPourArea(run.kind) ? runArea(run) : runLength(run) * (thick / 12);
        const perim = isPourArea(run.kind) ? runPerimeter(run) : runLength(run);
        const cy = isPourArea(run.kind)
          ? (area * (thick / 12)) / 27
          : (runLength(run) * (thick / 12) * (depth / 12)) / 27;
        if (cy <= 0) continue;

        lines.push(sRow(`${run.id}-mix`, mix, cy, from));
        lines.push(sRow(`${run.id}-pump`, "pump", cy, from));

        if (isPourArea(run.kind)) {
          const p = POUR_AREAS[run.kind];
          lines.push(sRow(`${run.id}-edge`, "form-edge", perim, from));
          if (p.slab) lines.push(sRow(`${run.id}-vapor`, "vapor", area, from));
          if (finish !== "none") lines.push(sRow(`${run.id}-fin`, finish, area, from));
          lines.push(sRow(`${run.id}-cure`, "cure-seal", area, from));
        } else {
          const p = POUR_LINES[run.kind];
          // both faces of the excavation or wall get formed
          lines.push(
            sRow(`${run.id}-form`, p.wall ? "form-wall" : "form-footing", 2 * runLength(run) * (depth / 12), from),
          );
        }

        /* ---- reinforcing that belongs to the pour -------------------- */
        if (isPourArea(run.kind) && mat !== "none") {
          if (mat.startsWith("wwr")) {
            // 10 % side and end lap on the sheets
            lines.push(sRow(`${run.id}-wwr`, mat, area * 1.1, from));
          } else {
            const [barId, spacingTxt] = mat.split("@");
            const spacing = Number(spacingTxt) / 12;
            // bars each way, plus 10 % for laps
            const barFt = spacing > 0 ? (area / spacing) * 2 * 1.1 : 0;
            lines.push(sRow(`${run.id}-mat`, barId, barFt, from));
            lines.push(sRow(`${run.id}-chair`, "chair", Math.ceil(area / 16), from));
          }
        }
        if (isPourLine(run.kind)) {
          const nBars = Math.round(num(Number(run.meta?.bars), 4));
          if (nBars > 0) {
            const barId = mat.startsWith("rb-") ? mat.split("@")[0] : "rb-5";
            lines.push(sRow(`${run.id}-cont`, barId, runLength(run) * nBars * 1.1, from));
            lines.push(sRow(`${run.id}-chair`, "chair", Math.ceil(runLength(run) / 4), from));
          }
        }
        continue;
      }

      /* ---- loose reinforcing bar drawn as a line -------------------- */
      if (isRebarRun(run.kind)) {
        const ft = runLength(run) * 1.1; // lap allowance
        lines.push(sRow(`${run.id}-bar`, run.kind, ft, from));
        lines.push(sRow(`${run.id}-barchair`, "chair", Math.ceil(runLength(run) / 4), from));
        continue;
      }

      /* ---- structural steel ----------------------------------------- */
      const it = getStructuralItem(run.kind);
      if (!it) continue;
      const ft = runLength(run);
      lines.push(sRow(`${run.id}-mem`, run.kind, ft, from));
      joints += Math.max(0, run.pts.length - 2) + 2; // each end plus every kink
      // shop primer follows the member surface, roughly 3 sq ft per foot
      lines.push(sRow(`${run.id}-prime`, "shop-prime", ft * 3, from));
    }

    if (joints) lines.push(sRow("joints", "field-weld", joints));

    /* ---- placed anchors, embeds and connection details -------------- */
    for (const m of doc.markers) {
      const it = getStructuralItem(m.kind);
      if (!it) continue;
      lines.push(sRow(`${m.id}-mk`, m.kind, 1, [m.mark]));
      if (m.kind === "base-plate") {
        // a set column base: four cast-in bolts and the grout under the plate
        lines.push(sRow(`${m.id}-ab`, "ab-3-4", 4, [m.mark]));
        lines.push(sRow(`${m.id}-grout`, "mix-grout", 0.015, [m.mark]));
      }
      if (m.kind === "embed") lines.push(sRow(`${m.id}-fw`, "field-weld", 1, [m.mark]));
    }

    return mergeLines(lines.filter((l): l is TakeoffLine => !!l));
  },
  summary(doc) {
    let cy = 0;
    let barFt = 0;
    let steelFt = 0;
    for (const r of doc.runs) {
      const t = num(Number(r.meta?.thick), 6);
      const d = num(Number(r.meta?.depth), 12);
      if (isPourArea(r.kind)) cy += (runArea(r) * (t / 12)) / 27;
      else if (isPourLine(r.kind)) cy += (runLength(r) * (t / 12) * (d / 12)) / 27;
      else if (isRebarRun(r.kind)) barFt += runLength(r);
      else steelFt += runLength(r);
    }
    const bits = [
      `${cy.toFixed(1)} cu yd concrete`,
      `${barFt.toFixed(0)} ft rebar`,
      `${steelFt.toFixed(1)} ft steel`,
      `${doc.markers.length} anchor${doc.markers.length === 1 ? "" : "s"} / detail${doc.markers.length === 1 ? "" : "s"}`,
    ];
    return bits.join(" · ");
  },
};

export const SKETCH_ADAPTERS: Record<string, TradeSketchAdapter> = {
  electrical: electricalSketchAdapter,
  duct: ductSketchAdapter,
  structural: structuralSketchAdapter,
};

/** Keeps STRUCTURAL_ITEMS referenced so a bad id fails loudly in dev. */
export const KNOWN_STRUCTURAL_IDS = new Set(STRUCTURAL_ITEMS.map((i) => i.id));
